import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router';
import Service1 from '../../assets/Service1.jpg';
import Service2 from '../../assets/Service2.jpg';
import Service3 from '../../assets/Service3.jpg';
import Service4 from '../../assets/Service4.jpg';

const treatments = [
  {
    title: 'Invisalign',
    description: 'Discreet, comfortable clear aligners to straighten your smile—no brackets or wires.',
    image: Service1,
    link: '/invisalign',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    title: 'Dental Implants',
    description: 'Replace missing teeth permanently with strong, natural-looking implants.',
    image: Service2,
    link: '/implants',
    icon: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    title: 'Emergency Dental Care',
    description: 'Same-day appointments to relieve pain and treat urgent dental issues.',
    image: Service3,
    link: '/emergency-care',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    title: 'Smile Makeover',
    description: 'Combine cosmetic treatments like veneers, whitening, and Invisalign for a full transformation.',
    image: Service4,
    link: '/smile_makeover',
    icon: 'M14 10l-2 1m-4 3l2-5 4-2 2 5-2 2m-4-3l-2 1m0 0l-6 3m6-3l-2 1m2 10l4-3m0 0l2-5-2-2-4 3m0 0l-6 3'
  }
];

const TreatmentCard = ({ treatment, imageLeft }) => {
  return (
    <motion.div 
      className={`flex flex-col md:flex-row items-center justify-between 
        ${imageLeft ? 'md:flex-row-reverse' : ''} 
        mb-16 space-y-6 md:space-y-0 md:space-x-12 
        relative overflow-hidden`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      {/* Background SVG Pattern */}
      <svg 
        className="absolute inset-0 w-full h-full opacity-5 z-0 text-teal-200" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path 
              d="M0 0L10 10L0 20" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="0.5"
            />
            <path 
              d="M10 0L20 10L10 20" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern)" />
      </svg>

      {/* Image Container */}
      <motion.div 
        className="w-full md:w-1/2 rounded-xl overflow-hidden shadow-lg relative z-10"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="absolute top-4 left-4 z-20">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-12 w-12 text-teal-500 bg-white rounded-full p-2 shadow-md"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d={treatment.icon} 
            />
          </svg>
        </div>
        <img 
          src={treatment.image} 
          alt={treatment.title} 
          className="w-full h-64 object-cover"
        />
      </motion.div>

      {/* Text Container */}
      <div 
        className={`w-full md:w-1/2 relative z-10 
        ${imageLeft ? 'md:ml-2' : 'md:mr-2'}`}
      >
        <h3 className="text-2xl font-bold text-teal-800 mb-4">
          {treatment.title}
        </h3>
        <p className="text-gray-600 mb-6">
          {treatment.description}
        </p>
        <Link 
          to={treatment.link} 
          className="inline-block px-6 py-3 
            bg-teal-500 text-white 
            rounded-full 
            hover:bg-teal-600 
            transition duration-300 
            transform hover:-translate-y-1 
            shadow-md hover:shadow-lg"
        >
          Learn More
        </Link>
      </div>
    </motion.div>
  );
};

function FeaturedTreatments() {
  return (
    <section className="py-20 px-6 bg-teal-50 relative">
      {/* Decorative SVG Background */}
      <svg 
        className="absolute inset-0 w-full h-full opacity-10 z-0 text-lime-200" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="logo-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path 
              d="M50 0 L100 50 L50 100 L0 50 Z" 
              fill="currentColor" 
              fillOpacity="0.1"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="url(#logo-pattern)" />
      </svg>

      <div className="max-w-6xl mx-auto text-center mb-16 relative z-10">
        <motion.h2 
          className="text-4xl font-bold text-teal-900 mb-4"
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Popular Treatments at a Glance
        </motion.h2>
        <motion.p 
          className="text-gray-600 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Explore some of our most requested services, trusted by patients for their results and comfort.
        </motion.p>
      </div>

      <div className="max-w-6xl mx-auto space-y-16 relative z-10">
        {treatments.map((treatment, index) => (
          <TreatmentCard 
            key={treatment.title} 
            treatment={treatment} 
            imageLeft={index % 2 !== 0} 
          />
        ))}
      </div>
    </section>
  );
}

export default FeaturedTreatments;